import type { NavigateFunction } from 'react-router-dom';

let navigatorRef: NavigateFunction | null = null;

export function setNavigator(navigate: NavigateFunction | null) {
  navigatorRef = navigate;
}

const NAV_STACK_KEY = 'navPaths';
const LAST_NAV_KEY = '/courses';

function readNavStack(): string[] {
  try {
    const raw = sessionStorage.getItem(NAV_STACK_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((p) => typeof p === 'string') : [];
  } catch (_) {
    return [];
  }
}

function writeNavStack(stack: string[]) {
  try {
    sessionStorage.setItem(NAV_STACK_KEY, JSON.stringify(stack));
  } catch (_) {
    // ignore
  }
}

function getCurrentLocation(): string {
  try {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  } catch (_) {
    return '/';
  }
}

export function clearNavStack() {
  writeNavStack([]);
}

/**
 * Navigate to a new path while pushing the current path to a stack in sessionStorage.
 * Works from anywhere (not limited to React components).
 */
export function smartNavigate(path: string): void {
  try {
    const current = getCurrentLocation();

    // Push current path to session stack
    const stack = readNavStack();
    stack.push(current);
    writeNavStack(stack);

  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('smartNavigate: unable to read or save current path', e);
  }

  // Prefer React Router navigate if available
  try {
    if (navigatorRef) {
      navigatorRef(path);
      return;
    }
  } catch (e) {
    // If navigate fails for some reason, fall back below
  }

  // Try client-side navigation via History API so we avoid full page reload
  try {
    if (typeof window !== 'undefined' && window.history && typeof window.history.pushState === 'function') {
      window.history.pushState({}, '', path);
      // Notify React Router about the location change
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }
  } catch (e) {
    // fall through to hard navigation
  }

  // Fallback: hard navigation
  if (typeof window !== 'undefined') {
    window.location.href = path;
  }
}

/**
 * Navigate back using stored stack when available, otherwise go back one step in history.
 */
export function smartGoBack(): void {
  // First, try to pop from our session stack
  try {
    const stack = readNavStack();
    const target = stack.pop();
    if (target && target.trim()) {
      writeNavStack(stack);
      // Prefer React Router navigate if available
      try {
        if (navigatorRef) {
          navigatorRef(target);
          return;
        }
      } catch (_) {
        // ignore and try fallbacks
      }

      // Fallback to History API navigation to specific path
      try {
        if (typeof window !== 'undefined' && window.history && typeof window.history.pushState === 'function') {
          window.history.pushState({}, '', target);
          window.dispatchEvent(new PopStateEvent('popstate'));
          return;
        }
      } catch (_) {
        // ignore and try hard navigation
      }

      // Hard navigation as last resort
      if (typeof window !== 'undefined') {
        window.location.href = target;
        return;
      }
    }
  } catch (_) {
    // ignore and proceed to generic back behavior
  }


  // Generic back behavior (history -1)
  try {
    if (navigatorRef) {
      navigatorRef(LAST_NAV_KEY);
      return;
    }
  } catch (_) {
    // ignore and try window.history
  }

  if (typeof window !== 'undefined' && window.history && typeof window.history.back === 'function') {
    window.history.back();
    return;
  }

  // Final fallback: navigate to root
  if (typeof window !== 'undefined') {
    window.location.href = '/';
  }
}
